import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useOrganizationActionFence } from '@/hooks/use-organization-action-fence.hook';
import {
  loadBillingPortalDestination,
  type BillingPortalTrigger,
} from '@/lib/billing-portal';

const navigateBrowser = (destination: string) => {
  window.location.href = destination;
};

export function useBillingPortalLauncher(
  organizationId: string,
  trigger: BillingPortalTrigger,
  navigate: (destination: string) => void = navigateBrowser
) {
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const inFlightRef = useRef(false);
  const { beginOrganizationAction, isOrganizationActionCurrent } =
    useOrganizationActionFence(organizationId);

  useEffect(() => {
    inFlightRef.current = false;
    setIsPortalLoading(false);
  }, [organizationId]);

  const openBillingPortal = useCallback(async () => {
    const action = beginOrganizationAction();
    if (!action || inFlightRef.current) return;
    inFlightRef.current = true;
    setIsPortalLoading(true);

    try {
      const destination = await loadBillingPortalDestination(
        action.organizationId,
        trigger
      );
      if (!isOrganizationActionCurrent(action)) return;
      navigate(destination);
    } catch (error) {
      if (!isOrganizationActionCurrent(action)) return;
      const message =
        (error as { data?: { detail?: string } })?.data?.detail ||
        (error as Error)?.message ||
        'Please try again.';
      toast.error(`Failed to open billing portal: ${message}`);
    } finally {
      if (isOrganizationActionCurrent(action)) {
        inFlightRef.current = false;
        setIsPortalLoading(false);
      }
    }
  }, [beginOrganizationAction, isOrganizationActionCurrent, navigate, trigger]);

  return { isPortalLoading, openBillingPortal };
}
